import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const NotesPage = () => {
  const navigate = useNavigate();
  const [notes, setNotes] = useState([]);
  const [expandedNote, setExpandedNote] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Load notes from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('preptune_notes');
    if (stored) {
      setNotes(JSON.parse(stored));
    }
  }, []);
  
  const deleteNote = (noteId) => {
    if (window.confirm('Delete this note?')) {
      const updated = notes.filter(n => n.id !== noteId);
      setNotes(updated);
      localStorage.setItem('preptune_notes', JSON.stringify(updated));
    }
  };
  
  const clearAllNotes = () => {
    if (window.confirm('Are you sure you want to clear all notes?')) {
      setNotes([]);
      localStorage.removeItem('preptune_notes');
    }
  };
  
  const filteredNotes = notes.filter(note => 
    note.content?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    note.summary?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    note.title?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  const getActionTypeColor = (actionType) => {
    switch (actionType) {
      case 'summarize': return '#3b82f6';
      case 'suggest': return '#10b981';
      case 'feedback': return '#f59e0b';
      case 'improve': return '#8b5cf6';
      default: return '#6b7280';
    }
  };
  
  const getActionTypeLabel = (actionType) => {
    switch (actionType) {
      case 'summarize': return '📋 Summary';
      case 'suggest': return '💡 Suggestions';
      case 'feedback': return '💬 Feedback';
      case 'improve': return '⚡ Improvements';
      default: return '📝 Note';
    }
  };
  
  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '20px'
    }}>
      <div style={{
        maxWidth: '900px',
        margin: '0 auto',
        background: 'white',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <button
            onClick={() => navigate('/voice-mode')}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              color: '#667eea',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontWeight: '500',
              padding: '8px 12px',
              borderRadius: '8px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(102, 126, 234, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Back to Chat
          </button>
          
          {notes.length > 0 && (
            <button
              onClick={clearAllNotes}
              style={{
                background: '#fee2e2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                padding: '8px 14px',
                cursor: 'pointer',
                color: '#dc2626',
                fontSize: '13px',
                fontWeight: '500',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#fecaca';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#fee2e2';
              }}
            >
              Clear All
            </button>
          )}
        </div>
        
        {/* Title */}
        <h1 style={{ 
          fontSize: '28px', 
          fontWeight: '600', 
          marginBottom: '8px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
          📝 My Notes
        </h1>
        
        <p style={{ 
          fontSize: '14px', 
          color: '#6b7280', 
          marginBottom: '24px' 
        }}>
          {notes.length} {notes.length === 1 ? 'note' : 'notes'} saved
        </p>
        
        {/* Search Bar */}
        {notes.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <input
              type="text"
              placeholder="🔍 Search notes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid #e5e7eb',
                borderRadius: '10px',
                fontSize: '14px',
                outline: 'none',
                transition: 'all 0.2s ease',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#667eea';
                e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
        )}
        
        {/* Notes List */}
        {filteredNotes.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '60px 20px', 
            color: '#6b7280' 
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📝</div>
            <p style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px', color: '#374151' }}>
              {searchQuery ? 'No notes found' : 'No notes yet'}
            </p>
            <p style={{ fontSize: '14px' }}>
              {searchQuery 
                ? 'Try a different search term' 
                : 'Start taking notes in chat by saying "take note"'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {filteredNotes.map(note => (
              <div
                key={note.id}
                style={{
                  background: expandedNote === note.id ? '#f9fafb' : 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '12px',
                  padding: '16px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onClick={() => setExpandedNote(expandedNote === note.id ? null : note.id)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#667eea';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {/* Note Header */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '12px',
                  gap: '12px'
                }}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#111827',
                      marginBottom: '4px'
                    }}>
                      {note.title}
                    </h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{ 
                        fontSize: '12px', 
                        color: '#6b7280',
                        fontWeight: '500'
                      }}>
                        {note.date} • {new Date(note.timestamp).toLocaleTimeString('en-US', {
                          hour: 'numeric',
                          minute: '2-digit'
                        })}
                      </span>
                      {note.actionType && (
                        <span style={{
                          fontSize: '11px',
                          fontWeight: '600',
                          color: getActionTypeColor(note.actionType),
                          background: `${getActionTypeColor(note.actionType)}15`,
                          padding: '2px 8px',
                          borderRadius: '6px'
                        }}>
                          {getActionTypeLabel(note.actionType)}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteNote(note.id);
                    }}
                    style={{
                      background: '#fee2e2',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '6px 12px',
                      cursor: 'pointer',
                      color: '#dc2626',
                      fontSize: '12px',
                      fontWeight: '500',
                      transition: 'all 0.2s ease',
                      flexShrink: 0
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#fecaca';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#fee2e2';
                    }}
                  >
                    Delete
                  </button>
                </div>
                
                {/* Tags */}
                {note.tags && note.tags.length > 0 && (
                  <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
                    {note.tags.map((tag, idx) => (
                      <span
                        key={idx}
                        style={{
                          fontSize: '11px',
                          color: '#6b7280',
                          background: '#f3f4f6',
                          padding: '3px 8px',
                          borderRadius: '6px',
                          fontWeight: '500'
                        }}
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
                
                {/* Note Content */}
                <div style={{
                  fontSize: '14px',
                  color: '#374151',
                  lineHeight: '1.6',
                  maxHeight: expandedNote === note.id ? 'none' : '80px',
                  overflow: 'hidden',
                  position: 'relative'
                }}>
                  {expandedNote === note.id ? (
                    <>
                      {/* Original Content */}
                      <div style={{
                        background: 'linear-gradient(135deg, #e0e7fc 0%, #f5e6ff 100%)',
                        padding: '12px',
                        borderRadius: '8px',
                        marginBottom: '12px',
                        fontSize: '13px',
                        lineHeight: '1.7',
                        whiteSpace: 'pre-wrap'
                      }}>
                        <div style={{
                          fontSize: '11px',
                          fontWeight: '600',
                          color: '#8b5cf6',
                          marginBottom: '8px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Original Content
                        </div>
                        {note.content}
                      </div>
                      
                      {/* AI Summary */}
                      <div style={{
                        fontSize: '13px',
                        color: '#374151',
                        padding: '12px',
                        background: '#f9fafb',
                        borderRadius: '8px',
                        borderLeft: '3px solid #667eea'
                      }}>
                        <div style={{
                          fontSize: '11px',
                          fontWeight: '600',
                          color: '#667eea',
                          marginBottom: '8px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          {note.actionType === 'suggest' ? 'AI Suggestions' :
                           note.actionType === 'feedback' ? 'AI Feedback' :
                           note.actionType === 'improve' ? 'AI Improvements' :
                           'AI Summary'}
                        </div>
                        {note.summary}
                      </div>
                    </>
                  ) : (
                    <>
                      {note.summary}
                      {note.summary && note.summary.length > 120 && (
                        <div style={{
                          position: 'absolute',
                          bottom: 0,
                          right: 0,
                          background: 'linear-gradient(to right, transparent, white 50%)',
                          padding: '0 8px 0 40px',
                          fontSize: '12px',
                          color: '#667eea',
                          fontWeight: '500'
                        }}>
                          Read more
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Help Text */}
        {notes.length === 0 && (
          <div style={{
            marginTop: '24px',
            padding: '16px',
            background: '#f0f9ff',
            border: '1px solid #bfdbfe',
            borderRadius: '8px',
            fontSize: '13px',
            color: '#1e40af',
            lineHeight: '1.6'
          }}>
            <strong style={{ display: 'block', marginBottom: '8px' }}>💡 How to use notes:</strong>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li>In chat, say "take note" or "start taking notes"</li>
              <li>Type your notes - each message is captured</li>
              <li>Say "stop taking note" or click stop when done</li>
              <li>AI will analyze and provide feedback based on context</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotesPage;